import { CommonModule, DatePipe } from '@angular/common';
import { Component, inject, OnInit, signal, ViewChild } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { LabdataService } from '../../services/labdata.service';
import { SiteDataCaptureSites } from '../../interfaces/siteType';
import { SiteDataService } from '../../services/sitedata.service';
import { AddeditSitedatacaptureComponent } from './addedit-sitedatacapture/addedit-sitedatacapture.component';
import { ConfirmationDialogComponent } from '../shared/components/confirmation-dialog/confirmation-dialog.component';
import { SiteDigesterGeneralDataResponse } from '../../interfaces/sdcmodel';
import { AlertDialogComponent } from '../shared/components/alert-dialog/alert-dialog.component';
import { RolePermissionsService } from '../../services/role-permissions.service';
import { LoadScheduleControlFlags, ScreenName } from '../../interfaces/securityPermissions';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { formatDateOnly } from '../shared/commonutils/helper';
import { RoleService } from '../../services/role.service';

@Component({
  selector: 'app-sitedatacapture',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCardModule,
    MatTabsModule,
    MatMenuModule,
    MatTableModule,
    MatDatepickerModule
  ],
  templateUrl: './sitedatacapture.component.html',
  styleUrl: './sitedatacapture.component.scss'
})
export class SitedatacaptureComponent implements OnInit { 
  permissions = inject(RolePermissionsService);
  rolesService= inject(RoleService);
  private dialog = inject(MatDialog);
  private service = inject(SiteDataService);
  displayedColumns: string[] = [ 'actions', 'id', 'siteName', 'dateCaptured', 'tic', 'vfa', 'ph', 'orp', 'createdBy'
  ];
  dataSource = new MatTableDataSource<any>();
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;  
  selectedSiteIdControl = new FormControl<number | null>(null);
  selectedSiteId = this.selectedSiteIdControl.valueChanges.pipe().subscribe((id) => {
    this.selectedSiteIdValue.set(id);
  });
  selectedSiteIdValue = signal<number | null>(null);
  sdcSites = signal<SiteDataCaptureSites[]>([]);
  datePipe: DatePipe = new DatePipe('en-US');
  range: FormGroup = new FormGroup({
    start: new FormControl<Date | null>(null),
    end: new FormControl<Date | null>(null),
  });
  controlPermissions: LoadScheduleControlFlags = new LoadScheduleControlFlags();
  ngOnInit() {
    const today = new Date();
    const firstDayOfCurrentWeek = this.getFirstDayOfWeek(today);
    firstDayOfCurrentWeek.setDate(today.getDate() - 7);
    today.setDate(today.getDate() + 2);

    const initialRange = new FormGroup({
      start: new FormControl<Date | null>(firstDayOfCurrentWeek),
      end: new FormControl<Date | null>(today)
    });
    this.range = initialRange;
    this.loadSites();
  }  

  loadSites(){
    this.service.getsdcSites().subscribe((response) => {
      this.sdcSites.set(response);
      this.onLoadData();
    });
        
    let roles = this.rolesService.getRolesAsString();
    this.permissions.getRolePermissionsNew(roles, ScreenName.SiteDataCapture, 0).subscribe({
      next: (perms: LoadScheduleControlFlags) => {
        this.controlPermissions = perms;
      }
    })
  }

  onLoadData(sid: string | null = null){    
    this.range.controls['start'].markAsDirty();
    this.range.controls['start'].markAsTouched();
    this.range.controls['end'].markAsDirty();
    this.range.controls['end'].markAsTouched();
    this.range.controls['start'].updateValueAndValidity();
    this.range.controls['end'].updateValueAndValidity();
    this.range.markAllAsTouched();
    this.range.updateValueAndValidity();
    
    if (this.range.invalid) {
      this.dialog.open(AlertDialogComponent, {
        data: {
          title: 'Invalid Date Range',
          message: 'Please select a valid date range.'
        },
        disableClose: true,
        panelClass: 'delete-confirmation-dialog-nowrap'
      });
      return;
    }
    if (this.range.invalid || !this.range.value.start || !this.range.value.end) {
      this.dialog.open(AlertDialogComponent, {
        data: {
          title: 'Invalid Date Range',
          message: 'Please select a valid date range.'
        },
        disableClose: true,
        panelClass: 'delete-confirmation-dialog-nowrap'
      });
      return;
    }
    
    const fromDate = this.range.value.start ? formatDateOnly(this.range.value.start) : '';
    const toDate = this.range.value.end ? formatDateOnly(this.range.value.end) : '';
    this.service.getsdcData(sid, fromDate, toDate).subscribe((response) => {
      console.log("sdc grid data", response);
      this.dataSource.data = response;
      this.dataSource.paginator = this.paginator;
      this.dataSource.sort = this.sort;
    });     
  }

  onSiteChange(){
    const siteId = this.selectedSiteIdValue();
    if(siteId !== null){
      this.onLoadData(`VRS-${siteId}`);
    }
    else{
      this.onLoadData();
    }
  }  

  onSearch(){
    const siteId = this.selectedSiteIdValue();
    if(siteId !== null){
      this.onLoadData(`VRS-${siteId}`);
    }else{
      this.onLoadData();
    }
  }

  onAddOrEdit(row: SiteDigesterGeneralDataResponse | null, action: string){
    if(action === 'Add' && this.selectedSiteIdValue() === null){
      this.dialog.open(AlertDialogComponent, {
        data: {
          title: 'Alert',
          message: 'Please select a site before Data Capture begin !!!',
          buttonText: 'OK'
        },
        disableClose: true,
        panelClass: 'delete-confirmation-dialog-nowrap'
      }).afterClosed().subscribe(() => {});
      return;
    }
    var userName = action === 'Edit' ? row?.createdBy === null ? localStorage.getItem('loggedUserName') : row?.createdBy : localStorage.getItem('loggedUserName');
    if(userName === null){
      userName = localStorage.getItem('loggedUserName');
    }
    this.dialog.open(AddeditSitedatacaptureComponent, {
      data: {
        siteId: row ? 'VRS-' + row.siteId : 'VRS-'+this.selectedSiteIdValue(),
        dateCaptured: row ? row.dateCaptured : new Date(),
        isEdit: action === 'Edit' ? true : false,
        siteName: action === 'Edit' ? row?.siteName : this.sdcSites().find(s => s.id === this.selectedSiteIdValue())?.siteName,
        userName: userName,
        id: row ? row.id : 0,
        canShowSaveButton : this.controlPermissions.addButton || this.controlPermissions.edit
      }
    }).afterClosed().subscribe((response: any) => {
      console.log('Dialog closed with response:', response);
      if(response){
        this.onSiteChange();
      }
    });
  }

  onDelete(row: SiteDigesterGeneralDataResponse){
    this.dialog.open(ConfirmationDialogComponent, {
      panelClass: 'delete-confirmation-dialog',
      data: {
        title: 'Confirm Deletion',
        message: `Are you sure you want to delete the Site Data Capture entry for "${row.siteName}" captured on "${this.datePipe.transform(row.dateCaptured, 'MM/dd/yyyy')}"?`,
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel'
      }
    }).afterClosed().subscribe((confirmed) => {
      if(confirmed){
        this.service.deletesiteDataCaptureData(row.id).subscribe((response) => {
          this.dataSource.data = this.dataSource.data.filter(d => d.id !== row.id);          
          if(response){
            this.onSiteChange();
          }
        });
      }
    });
  }
  

  getFirstDayOfWeek(date: Date) {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day;
    d.setDate(diff);
    return d;
  }

  onKeydown(event: KeyboardEvent) {
    if(event.ctrlKey && event.metaKey){return;} 
    if(!/[0-9\/]/.test(event.key) && event.key.length === 1){
      event.preventDefault();
    }
    setTimeout(() => {
      this.range.updateValueAndValidity();
      this.range.markAllAsTouched();
    });
  }

  dateformat(event: Event){
    const input = event.target as HTMLInputElement;
    let value = input.value;

    if(!value) return;
    const parts = value.split('/');
    if(parts.length !== 3) return;

    let [month, day, year] = parts.map(part => part.padStart(2, '0'));
    if(month.length === 1)  month = '0' + month;
    if(day.length === 1)  day = '0' + day;
    if(year.length === 2) year = '20' + year;
    input.value = `${month}/${day}/${year}`;
  }
}
